import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    FiDatabase,
    FiActivity,
    FiLayers,
    FiTrendingUp,
    FiHash,
    FiEyeOff,
    FiPercent,
    FiShuffle,
    FiMaximize2,
    FiMinimize2,
    FiExternalLink,
    FiAlertTriangle,
    FiChevronRight
} from 'react-icons/fi';
import { Tooltip } from '../../../../../components/ds';
import {
    resolveNameDetail,
    resolveDisplayName,
    resolveBooleanInputs,
    resolveMathInputs as resolveMathInputsUtil,
    resolveLogicInputs as resolveLogicInputsUtil,
    isModelOutputDict,
    buildModelTile,
    AUTHORING_GROUP,
    groupAuthoringItems,
    LOGIC_SUBGROUP,
    collectTriggerTree,
    flattenTriggerTree,
    buildConditionDetail,
    nodeDirection
} from '../utils/nodeUtils';
import ConditionRailTree from './ConditionRailTree';
import { SubgroupHeader } from './StrategyToolbox';
import BacktestTradeLog from '../../Backtesting/components/BacktestTradeLog';
import BacktestAnalyticsPanel from '../../Backtesting/components/BacktestAnalyticsPanel';
import BacktestPerformance from '../../Backtesting/components/BacktestPerformance';
import { recalcMetricsFromTrades } from '../../../../../utils/backtestMetricsCalc';
import { isNodeShownOnChart } from '../config/nodeVisualConfig';
import { useBreakpoint } from '../../../../../hooks/useBreakpoint';
import { buildTradeSpanMap, levelsAtBar } from '../../../../../utils/tradeLevelUtils';

//! 50-15: stable default so React.memo is not defeated by a fresh {} per render.
const EMPTY_CATEGORY_MAP = Object.freeze({});

const extractNodeValue = (rawValue) => {
    if (typeof rawValue !== 'object' || rawValue === null) return rawValue;
    //! Entry signal nodes return a rich output when fired:
    //!   { triggered: true, entry_price, stage, side }
    //!  graph_evaluator.py:1448-1456 — only emitted when the signal is TRUE.
    //!  Without this branch, fired entries normalize to null (WAIT) instead of TRUE.
    if ('triggered' in rawValue) return rawValue.triggered;
    //! EvaluatedNode envelope shape — most callers already unwrap via the
    //!  useLiveExecution flatten, but defensive in case raw envelopes leak through.
    if ('value' in rawValue) return rawValue.value;
    return undefined;
};

const normalizeBooleanStatus = (rawValue) => {
    const value = extractNodeValue(rawValue);

    if (value === undefined || value === null) return null;
    if (typeof value === 'boolean') return value;

    if (typeof value === 'number') {
        if (value === 1) return true;
        if (value === 0) return false;
        return null;
    }

    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (['true', '1'].includes(normalized)) return true;
        if (['false', '0'].includes(normalized)) return false;
    }

    return null;
};

const resolveNodeStatusRaw = (nodeId, hoverData, executionData, mergedData) => {
    const hoverValue = hoverData?.[nodeId];
    if (hoverValue !== undefined && hoverValue !== null) return hoverValue;

    //! 50-17: hovering a bar -> the node's value AT THAT BAR. Only chart-layer nodes (ELIGIBLE_LAYER_NODE_TYPES)
    //*   ride hoverData; every other functional node (time_window, constant) has its per-bar series in
    //*   indicatorMaps, which `mergedData` already resolves at the hovered time. The live executionData is the
    //*   latest bar's value and must never stand in for a hovered bar.
    if (hoverData?.time != null) {
        const hoveredBarValue = mergedData?.[nodeId];
        return hoveredBarValue === null ? undefined : hoveredBarValue;
    }

    const executionValue = executionData?.[nodeId];
    if (executionValue !== undefined && executionValue !== null) return executionValue;

    const mergedValue = mergedData?.[nodeId];
    if (mergedValue !== undefined && mergedValue !== null) return mergedValue;

    return undefined;
};

const shouldSuppressBooleanWarmup = (nodeId, hoverTime, booleanNodeIds) => {
    if (!hoverTime) return false;
    return booleanNodeIds.has(String(nodeId)) || /^(comparator|logic_gate|signal|position_trigger)_/.test(String(nodeId));
};

export const __rdwatchTestables = {
    extractNodeValue,
    normalizeBooleanStatus,
    resolveNodeStatusRaw,
    shouldSuppressBooleanWarmup
};

// Compact Section container
//  mode 'strip'     — legacy fixed-width column (default consumers)
//  mode 'grid'      — responsive grid cell (no fixed widths; the grid track sizes it)
//  mode 'accordion' — phone: button header (onToggle) + body only while open; without onToggle a static section
const DashboardSection = ({ title, icon: Icon, children, color = "gray", width = "w-auto", mode = 'strip', open = true, onToggle = null, sectionId = null }) => {
    if (mode === 'grid') {
        return (
            <div className="flex flex-col min-w-0 border border-[var(--divider)] rounded bg-bg-2">
                <div className="flex items-center gap-2 px-3 py-2 border-b border-[var(--divider)] bg-bg-3">
                    <Icon size={12} className={`text-${color}-500`} />
                    <span className="text-[10px] font-bold text-fg-3 uppercase tracking-widest truncate">
                        {title}
                    </span>
                </div>
                <div className="p-2 min-w-0">
                    {children}
                </div>
            </div>
        );
    }
    if (mode === 'accordion') {
        const headerInner = (
            <>
                <Icon size={12} className={`text-${color}-500`} />
                <span className="text-[10px] font-bold text-fg-3 uppercase tracking-widest truncate">
                    {title}
                </span>
            </>
        );
        return (
            <div className="border-b border-[var(--divider)] bg-bg-2 min-w-0">
                {onToggle ? (
                    <button
                        type="button"
                        id={`${sectionId}-header`}
                        aria-expanded={open}
                        aria-controls={`${sectionId}-body`}
                        onClick={onToggle}
                        className="w-full min-h-[44px] flex items-center gap-2 px-3 bg-bg-3 text-left"
                    >
                        {headerInner}
                        <FiChevronRight size={14} className={`ml-auto shrink-0 text-fg-3 transition-transform ${open ? 'rotate-90' : ''}`} />
                    </button>
                ) : (
                    <div className="flex items-center gap-2 px-3 py-2 bg-bg-3">{headerInner}</div>
                )}
                {(!onToggle || open) && (
                    <div
                        id={onToggle ? `${sectionId}-body` : undefined}
                        role={onToggle ? 'region' : undefined}
                        aria-labelledby={onToggle ? `${sectionId}-header` : undefined}
                        className="p-2 min-w-0"
                    >
                        {children}
                    </div>
                )}
            </div>
        );
    }
    return (
        <div className={`flex flex-col border-r border-[var(--divider)] bg-bg-2 ${width} min-w-[150px] shrink-0 h-full`}>
            {/* Compact Header */}
            <div className="flex items-center gap-2 px-3 py-2 border-b border-[var(--divider)] bg-bg-3">
                <Icon size={12} className={`text-${color}-500`} />
                <span className="text-[10px] font-bold text-fg-3 uppercase tracking-widest truncate">
                    {title}
                </span>
            </div>
            {/* Compact Content Body */}
            <div className="p-2 overflow-y-auto custom-scrollbar flex-1">
                {children}
            </div>
        </div>
    );
};

// Ultra-Compact Numeric Card (Label Over Value)
const CompactIsoCard = React.memo(({ label, value, color = "blue", onClick, isActive, badge, isHidden = false }) => (
    <div
        onClick={onClick}
        className={`flex flex-col rounded border p-1.5 mb-1.5 last:mb-0 transition-all cursor-pointer ${
 isActive
            ? `bg-${color}-50 dark:bg-${color}-900/20 border-${color}-500 ring-1 ring-${color}-500/50`
            : `bg-bg-1 border-[var(--divider)] hover:border-[color-mix(in_oklch,var(--node-visual)_30%,transparent)] hover:shadow-sm`
        } ${isHidden ? 'opacity-60' : ''}`}
    >
        <div className="flex items-center justify-between w-full overflow-hidden">
            <div className="flex items-center gap-1 flex-1 overflow-hidden">
                {isHidden && <FiEyeOff size={10} className="text-fg-3 shrink-0" title="Hidden from chart" />}
                <span className="text-[9px] font-bold text-fg-3 uppercase truncate">{label}</span>
            </div>
            {badge && (
                <span className="text-[8px] font-bold bg-[color-mix(in_oklch,var(--warning)_16%,transparent)] text-[var(--warning)] px-1 rounded shrink-0 ml-1">
                    {badge}
                </span>
            )}
        </div>
        <span className={`text-sm font-mono font-bold text-fg-1 truncate`}>
            {typeof value === 'number'
                ? value.toFixed(2)
                : (typeof value === 'object' && value !== null
                    ? (Array.isArray(value) ? 'ARRAY' : 'OBJECT')
                    : (value !== undefined && value !== null ? value : '---'))}
        </span>
    </div>
));

// Comprehensive Composite Card for Multi-value indicators (MACD, Bollinger, etc.)
const CompositeIndicatorCard = React.memo(({ label, labelTitle, values, color = "purple", onClick, isActive, badge, isHidden = false, primaryValue, highlightKey, annotations }) => (
    <div
        onClick={onClick}
        className={`flex flex-col rounded border p-1.5 mb-1.5 last:mb-0 transition-all cursor-pointer ${
 isActive
            ? `bg-${color}-50 dark:bg-${color}-900/20 border-${color}-500 ring-1 ring-${color}-500/50`
            : `bg-bg-1 border-[var(--divider)] hover:border-[color-mix(in_oklch,var(--node-visual)_30%,transparent)] hover:shadow-sm`
        } ${isHidden ? 'opacity-60' : ''}`}
    >
        <div className="flex items-center justify-between w-full overflow-hidden mb-1">
            <div className="flex items-center gap-1 flex-1 overflow-hidden">
                {isHidden && <FiEyeOff size={10} className="text-fg-3 shrink-0" title="Hidden from chart" />}
                <span className="text-[9px] font-bold text-fg-3 uppercase truncate" title={labelTitle || undefined}>{label}</span>
            </div>
            {badge && (
                <span className="text-[8px] font-bold bg-[color-mix(in_oklch,var(--warning)_16%,transparent)] text-[var(--warning)] px-1 rounded shrink-0 ml-1">
                    {badge}
                </span>
            )}
        </div>

        {primaryValue !== undefined && (
            <div className="mb-1">
                <span className={`text-sm font-mono font-bold text-fg-1 truncate`}>
                    {typeof primaryValue === 'number' ? primaryValue.toFixed(2) : (primaryValue ?? 'WAIT')}
                </span>
            </div>
        )}
        <div className={`grid grid-cols-2 gap-x-2 gap-y-1.5 ${primaryValue !== undefined ? 'border-t border-[var(--divider)] pt-1.5 mt-0.5' : ''}`}>
            {Object.entries(values).map(([k, v]) => (
                <div key={k} className="flex flex-col min-w-0">
                    <span className="text-[7px] font-bold text-fg-3 uppercase truncate leading-tight">{k.replace('_', ' ')}</span>
                    {annotations?.[k] !== undefined ? (
                        <span data-testid={`highlight-${k}`} className="text-[10px] font-mono font-extrabold leading-none truncate text-[var(--warning)]">{annotations[k]}</span>
                    ) : (
                    <span className={`text-[10px] font-mono leading-none truncate ${k === highlightKey ? 'font-extrabold' : 'font-bold'} ${
 k.toLowerCase().includes('hist')
                        ? ((v || 0) >= 0 ? 'text-[var(--color-profit)]' : 'text-[var(--color-loss)]')
                        : 'text-fg-2'
                    }`}>
                        {typeof v === 'boolean'
                            ? (v ? 'TRUE' : 'FALSE')
                            : (typeof v === 'number'
                                ? v.toFixed(2)
                                : (typeof v === 'object' && v !== null
                                    ? (Array.isArray(v) ? 'ARR' : 'OBJ')
                                    : (v ?? 'WAIT')))}
                    </span>
                    )}
                </div>
            ))}
        </div>
    </div>
));

//* 50-21 R10-T3 — the ONE name rule for every condition / group / math label on this dashboard: the
//  display-name resolver (record custom_name > legacy custom data.label > current auto text). The CURRENT
//  auto-assembled text rides along as the tooltip only while a record custom_name is set.
const EMPTY_RECORD = Object.freeze({});
const displayLabelOf = (node, nodes, edges, visualOverrides) => {
    const entry = visualOverrides?.[node?.id];
    const name = resolveDisplayName(node, nodes, edges, entry, visualOverrides);
    return { name, autoText: resolveNameDetail(node, nodes, edges, entry, visualOverrides).autoText };
};

// Ultra-Compact Logic Row (Indicator style)
const CompactLogicRow = React.memo(({ label, labelTitle, value, onClick, isActive, operator }) => (
    <div
        onClick={onClick}
        className={`flex items-center justify-between py-1 border-b last:border-0 rounded px-1 transition-all cursor-pointer ${
 isActive
            ? 'bg-[color-mix(in_oklch,var(--node-visual)_10%,transparent)] border-[color-mix(in_oklch,var(--node-visual)_40%,transparent)]'
            : 'border-[var(--divider)] hover:bg-bg-3 dark:hover:bg-bg-2/50'
        }`}
    >
        <span className="text-[10px] font-medium text-fg-3 truncate max-w-[65%]" title={labelTitle || undefined}>{label}</span>
        <div className="flex items-center gap-1.5">
            {operator && (
                <span className="text-[8px] font-bold text-fg-3 uppercase tracking-tight">{operator}</span>
            )}
            <span className={`text-[8px] font-bold uppercase tracking-tight ${
 value === undefined || value === null
                    ? 'text-fg-3'
                    : value
                        ? 'text-[var(--color-profit)]'
                        : 'text-[var(--color-loss)]'
            }`}>
                {value === undefined || value === null ? 'WAIT' : (value ? 'TRUE' : 'FALSE')}
            </span>
            <div className={`w-3 h-3 rounded-sm transition-colors duration-300 ${
 value === undefined || value === null
                    ? 'bg-bg-3 dark:bg-bg-3'
                    : value
                        ? 'bg-[var(--color-profit)] shadow-emerald-500/20 shadow-sm'
                        : 'bg-[var(--color-loss)] shadow-red-500/20 shadow-sm'
            }`}></div>
        </div>
    </div>
));

//* Board #73 — always-on group-header for "Strategy Logic". The PositionGroup
//  rows below render PRE-GATE graph truth (node_results) — a node can read
//  TRUE there even though the engine suppressed the signal (Symptom #1: "LONG
//  ENTRY: TRUE" rendered as if it were a decision). This label renders
//  UNCONDITIONALLY, including when engineDecision is null (old worker
//  mid-deploy, or a bar with no triggered signal node) — that unconditional
//  render is what makes the surface honest rather than merely richer. The
//  verdict badge below it is the ONLY place in this file the real decision
//  is shown, and it is sourced from the `engineDecision` prop alone — never
//  from resolveNodeStatusRaw.
const EngineDecisionHeader = ({ engineDecision }) => (
    <div className="mb-2 px-1.5 py-1 rounded bg-bg-3/50 border border-[var(--divider)]">
        <div className="text-[8px] font-bold uppercase tracking-wider text-fg-3">
            Conditions preview — engine decision shown separately
        </div>
        {engineDecision && (
            <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                <span className={`text-[9px] font-bold uppercase tracking-tight ${
 engineDecision.verdict === 'emitted' ? 'text-[var(--color-profit)]' : 'text-[var(--warning)]'
                }`}>
                    {engineDecision.verdict === 'emitted' ? 'Emitted' : 'Suppressed'}
                </span>
                {engineDecision.action && (
                    <span className="text-[9px] font-mono text-fg-2 truncate">{engineDecision.action}</span>
                )}
                {engineDecision.reason && (
                    <span className="text-[8px] text-fg-3 truncate" title={engineDecision.reason}>
                        {engineDecision.reason}
                    </span>
                )}
            </div>
        )}
    </div>
);

//--> ONE operator display rule (GT -> '>'), shared by the Entry/Exit tree rows and the flat compact rows.
const OP_MAP = { 'GT': '>', 'LT': '<', 'GTE': '>=', 'LTE': '<=', 'EQ': '==', 'NEQ': '!=' };
const opDisplayOf = (node) => {
    const rawOp = node.data.operator || node.data.params?.operator;
    if (!rawOp) return null;
    const opKey = String(rawOp).toUpperCase();
    return OP_MAP[opKey] || opKey;
};

const TRIGGER_TYPES = ['signal', 'position_trigger'];
const DASH_ACCENT = {
    long: { dot: 'bg-[var(--color-profit)]', triggerReady: 'text-[var(--color-profit)]' },
    short: { dot: 'bg-[var(--color-loss)]', triggerReady: 'text-[var(--color-loss)]' },
};

// Grouping wrapper for Position Logic
const PositionGroup = ({ title, nodes, hoverData, mergedData, type, onItemClick, selectedNodeId, allNodes, allEdges, executionData, latestData, liveNodeInvalidReasons, liveNodeOutputs, positionLevels, tradeSpanMap, valueAt, latestBarTime, visualOverrides = EMPTY_RECORD }) => {
    //* 50-17: each trigger's condition tree comes from the ONE walk (collectTriggerTree), built lazily and keyed on
    //  the graph only. `nodes` is not a dependency (a fresh array each render), and live results are not either:
    //  a WS tick re-annotates at render and never re-walks the graph.
    const treeFor = React.useMemo(() => {
        const cache = new Map();
        return (id) => {
            if (!cache.has(id)) {
                const tree = collectTriggerTree(id, allNodes, allEdges);
                //  a self-contained trigger (D25) is rendered with an EMPTY tree: stray comparator edges never gate it
                cache.set(id, { tree, emptyTree: { ...tree, children: [] } });
            }
            return cache.get(id);
        };
    }, [allNodes, allEdges]);

    //--> row labels per graph identity: a WS tick does not rescan the edges for every row
    const { nodeById, labelOf, autoTextOf } = React.useMemo(() => {
        const byId = new Map((allNodes || []).map((n) => [n.id, n]));
        const labels = new Map();
        const autos = new Map();
        byId.forEach((n, id) => {
            const { name, autoText } = displayLabelOf(n, allNodes, allEdges, visualOverrides);
            labels.set(id, name);
            autos.set(id, autoText);
        });
        return { nodeById: byId, labelOf: (id) => labels.get(id), autoTextOf: (id) => autos.get(id) };
    }, [allNodes, allEdges, visualOverrides]);

    if (!nodes || nodes.length === 0) return null;

    const isAnyActive = nodes.some(node => {
        if (!TRIGGER_TYPES.includes(node.type)) return false;
        const rawStatus = resolveNodeStatusRaw(node.id, hoverData, executionData, mergedData);
        return normalizeBooleanStatus(rawStatus) === true;
    });

    //--> D25 live values: LTP from the market data; stop / target from `positionLevels` — the SAME open-trade
    //*   levels the chart's SL/TP price lines draw (openLevelsBySide over levelHistory). Absent (builder /
    //*   backtest) -> LTP only, today's text. A level is attributed to a row only when that side has exactly
    //*   ONE self-contained SL (TP) trigger — the Monitoring guard (SignalEvaluationPanel SideColumn).
    //!   50-17: in-position / flat comes from the position AUTHORITY, never from a missing level: hovering a bar
    //!   -> the trade the chart's hover band shows at that bar (levelsAtBar = SmartChart's findTradeAtTime);
    //!   not hovering -> the OMS position record (positionLevels.held = heldSideOf(positions)).
    const hoverTime = hoverData?.time ?? null;
    const baseLive = { ltp: mergedData?.ltp ?? null, barTime: hoverTime ?? latestBarTime };
    const liveFor = (node) => {
        const action = node?.data?.action;
        if (!positionLevels || (action !== 'sl' && action !== 'tp')) return baseLive;
        const side = nodeDirection(node);
        if (!side) return baseLive;
        let lv;
        if (hoverTime != null) {
            const at = levelsAtBar(tradeSpanMap, hoverTime);
            if (!at || at.side !== side) return { ...baseLive, noPosition: 'no position at this bar' };
            lv = at;
        } else {
            if (positionLevels.held !== side) return { ...baseLive, noPosition: true };
            lv = positionLevels[side] || { stop: null, target: null };
        }
        const selfContainedOf = (act) => (allNodes || []).filter((n) => TRIGGER_TYPES.includes(n.type)
            && n.data?.action === act && nodeDirection(n) === side && !!buildConditionDetail(n)?.text).length;
        const soleSl = selfContainedOf('sl') === 1;
        const soleTp = selfContainedOf('tp') === 1;
        return {
            ...baseLive,
            //* in position is asserted only where the level is attributable to THIS row (the sole trigger)
            inPosition: action === 'sl' ? soleSl : soleTp,
            stop: soleSl ? lv.stop : null,
            target: soleTp ? lv.target : null,
        };
    };
    const statusOf = (id) => normalizeBooleanStatus(resolveNodeStatusRaw(id, hoverData, executionData, mergedData));

    //--> Plan each trigger once: which of them render as a tree, and which nodes those trees cover.
    const plans = new Map();
    const covered = new Set();
    nodes.forEach((node) => {
        if (!TRIGGER_TYPES.includes(node.type)) return;
        const cd = buildConditionDetail(node, liveFor(node));
        const selfContained = !!cd?.text;
        const { tree, emptyTree } = treeFor(node.id);
        if (selfContained || tree.children.length > 0) {
            plans.set(node.id, { cd, tree: selfContained ? emptyTree : tree });
            if (!selfContained) flattenTriggerTree(tree).forEach((id) => covered.add(id));
        }
    });

    //--> value of a node for a comparator / gate detail: hovered bar (hoverData -> per-bar series) | live outputs -> execution -> merged; then constant node
    const condValueOf = (id) => {
        const src = nodeById.get(id);
        //* 50-17: hovering -> the hovered bar's value (hoverData, else the per-bar series via mergedData), never live
        let v = hoverTime != null
            ? (hoverData?.[id] ?? mergedData?.[id])
            : (liveNodeOutputs?.[id] ?? executionData?.[id] ?? mergedData?.[id]);
        if (v == null && src?.type === 'constant') v = src.data?.value;
        if ((src?.type === 'comparator' || src?.type === 'logic_gate') && typeof v === 'number') v = !!v;
        return v;
    };

    const rowOf = (id) => {
        const node = nodeById.get(id);
        const value = statusOf(id);
        //* the wire's invalid_reason is the LIVE bar's fact — a hovered bar with a value is not withheld
        const invalidReason = (hoverData?.[id] != null || (hoverTime != null && value !== null)) ? null : (liveNodeInvalidReasons?.[id] ?? null);
        //--> self-contained nodes and time windows own row text; comparator / logic_gate rows stay compact (text null)
        //    but carry the click-to-reveal `detail` with the values at the hovered bar, else live (the ONE authority).
        const isCondition = node && (node.type === 'comparator' || node.type === 'logic_gate');
        const ownsText = node && (TRIGGER_TYPES.includes(node.type) || node.type === 'time_window');
        const cd = (ownsText || isCondition) ? buildConditionDetail(node, isCondition ? { ...baseLive, valueOf: condValueOf, valueAt, outcome: invalidReason ? null : value } : { ...liveFor(node), ...(node?.type === 'time_window' ? { outcome: invalidReason ? null : value } : {}) }) : null;
        const hasText = !!cd?.text;
        return {
            name: labelOf(id),
            autoText: autoTextOf(id),
            //--> 50-21 Live-Val: a named row's operator belongs to the auto sentence (autoText), not after the authored name
            operatorText: node && !autoTextOf(id) ? opDisplayOf(node) : null,
            conditionText: hasText ? cd.text : undefined,
            detail: (hasText || isCondition) ? (cd?.detail ?? undefined) : undefined,
            value,
            invalidReason,
            chipText: value === null ? 'WAIT' : (value ? 'TRUE' : 'FALSE'),
            railState: invalidReason ? 'stale' : value === null ? 'wait' : (value ? 'met' : 'miss'),
        };
    };

    return (
        <div className="mb-3 last:mb-0">
             {title && (
             <div className={`text-[9px] font-bold uppercase tracking-wider mb-1 px-1.5 py-0.5 rounded transition-all duration-300 ${
 isAnyActive
                 ? (type === 'long' ? 'bg-[color-mix(in_oklch,var(--color-profit)_12%,transparent)] text-[var(--color-profit)] shadow-[0_0_10px_rgba(16,185,129,0.1)]' : 'bg-[color-mix(in_oklch,var(--color-loss)_12%,transparent)] text-[var(--color-loss)] shadow-[0_0_10px_rgba(239,68,68,0.1)]')
                 : (type === 'long' ? 'text-[color-mix(in_oklch,var(--color-profit)_40%,transparent)]' : 'text-[color-mix(in_oklch,var(--color-loss)_40%,transparent)]')
             }`}>
                 {title}
             </div>
             )}
             <div className="bg-bg-2 border border-[var(--divider)] rounded p-1">
                 {nodes.map(node => {
                     const isSignal = TRIGGER_TYPES.includes(node.type);
                     const plan = isSignal ? plans.get(node.id) : null;

                     //--> A trigger with conditions (or its own self-contained condition) renders the shared tree.
                     if (plan) {
                         const statusValue = statusOf(node.id);
                         return (
                             <div key={node.id} className="mb-1 last:mb-0">
                                 <ConditionRailTree
                                     tree={plan.tree}
                                     rowOf={rowOf}
                                     accent={DASH_ACCENT[type] || DASH_ACCENT.long}
                                     flow={statusValue === true ? 'accent' : null}
                                     onRowClick={onItemClick}
                                     selectedId={selectedNodeId}
                                     triggerRow={{
                                         name: labelOf(node.id),
                                         //  Board #73: pre-gate graph truth, never 'fired'
                                         state: statusValue === true ? 'ready' : statusValue === false ? 'waiting' : 'wait',
                                         statusLabel: statusValue === null ? 'WAIT' : (statusValue ? 'TRUE' : 'FALSE'),
                                         conditionText: plan.cd?.text,
                                         conditionDetail: plan.cd?.detail,
                                     }}
                                 />
                             </div>
                         );
                     }
                     //--> Rows a tree already shows are not repeated flat.
                     if (covered.has(node.id)) return null;

                     const opDisplay = opDisplayOf(node);
                     const booleanInputs = resolveBooleanInputs(node, allNodes, allEdges, mergedData, executionData, latestData, visualOverrides);
                     const hasBooleanInputs = Object.keys(booleanInputs).length > 0;
                     const statusValue = statusOf(node.id);

                     return (
                         <div key={node.id} className="mb-1 last:mb-0">
                            <CompactLogicRow
                                label={labelOf(node.id)}
                                labelTitle={autoTextOf(node.id)}
                                operator={isSignal ? null : opDisplay}
                                value={statusValue}
                                onClick={() => onItemClick && onItemClick(node)}
                                isActive={selectedNodeId === node.id}
                            />
                            {hasBooleanInputs && (
                                <div className="ml-3 pl-2 border-l border-[var(--divider)] mt-0.5 space-y-0.5 pb-1">
                                    {Object.entries(booleanInputs).map(([label, val]) => (
                                        <div key={label} className="flex items-center justify-between opacity-80">
                                            <span className="text-[8px] text-fg-3 truncate max-w-[70%]">{label}</span>
                                            <div className={`w-1.5 h-1.5 rounded-full ${val === null || val === undefined ? 'bg-[var(--fg-3)]' : val ? 'bg-[var(--color-profit)]' : 'bg-[var(--color-loss)]'}`}></div>
                                        </div>
                                    ))}
                                </div>
                            )}
                         </div>
                     );
                 })}
             </div>
        </div>
    );
};

const EXCLUDED_KEYS = new Set(['_id', 'instrument_token', 'created_at', 'received_at', 'timestamp', 'time', 'exchange_token', 'composite_key', 'instrument_id', 'symbol', 'exchange', 'instrument_type', 'timeframe', 'source', 'tick_type', 'value']);
const OHLC_KEYS = ['open', 'high', 'low', 'close', 'ltp', 'volume', 'change', 'change_percent'];
const DEPTH_KEYS = ['bid_prices', 'ask_prices', 'bid_qtys', 'ask_qtys', 'bid_orders', 'ask_orders', 'bid_price', 'ask_price', 'bid_qty', 'ask_qty', 'total_buy_qty', 'total_sell_qty'];
const COMMON_ANALYTICS = ['vwap', 'avg_trade_price', 'trade_count', 'open_interest', 'volume_delta', 'buy_volume', 'sell_volume', 'sweep_count', 'sweep_volume'];

const RDWatchDashboard = React.memo(({
    categorizedNodes,
    //* 50-21 R10-T3 — node id -> record entry ({custom_name, notes, ...}); the ONE source of condition names.
    //  The parent (Builder store / Live Analysis / pop-out read hook) loads it once; this view never fetches.
    visualOverrides = EMPTY_RECORD,
    latestData,
    executionData,
    //* 142-37 loop 2 — { nodeId: fullOutputsDict } for reduced multi-output nodes (model tile).
    liveNodeOutputs = null,
    //* 50-17 — { nodeId: invalid_reason } from the live node results: a withheld comparator / gate is value
    //  FALSE + invalid_reason, so the tree shows it as unknown (neutral) instead of unsatisfied.
    liveNodeInvalidReasons = null,
    //* 50-17 — { long: {stop, target}|null, short: ..., held: 'long'|'short'|null (OMS position record),
    //  trades: compact chart trade segments } open-trade levels the chart draws (Live Analysis only). undefined = this view has no position feed (builder / backtest / pop-out) -> parameters-only rows.
    positionLevels = undefined,
    //! Board #73 — the engine's REAL post-gate verdict for the most recently
    //!  triggered signal node: { verdict: "emitted"|"suppressed", action, stage,
    //!  node_id, reason, since_ms }, or null when no signal node fired this bar
    //!  (E5a omit-when-absent convention — never render a false verdict for a
    //!  missing key). Rendered from THIS prop only — see the always-on label +
    //!  EngineDecisionBadge in the "Strategy Logic" section below. Must NEVER
    //!  be threaded into resolveNodeStatusRaw (hover→execution→merged, no lane
    //!  provenance) — that would inherit CF-2's ambiguity (design §3.E5 hard
    //!  boundary).
    engineDecision = null,
    hoverData,
    onPopOut,
    onItemClick,
    selectedNodeId,
    activeChartNodeId,
    primaryTimeframe,
    nodes,
    allIndicatorMetadata = [], // 142-37: output_labels authority for the model tile rows
    edges,
    indicatorMaps,
    backtestResults,
    backtestConfig,
    onPeriodSelect,
    isMetricsExpanded,
    onToggleMetricsExpanded,
    //* 50-12 opt-in: grid (>=768) / accordion (<768) market-data layout. Default false keeps the legacy strip.
    responsiveLayout = false,
    //* 50-14: panel instance (Live Analysis Dashboard tab) — accordion renderer at every tier and no left control
    //  column (Pop out / Maximize would be dead or drive the below-chart host's layout state). Default false = unchanged.
    panelMode = false,
    //* 50-15: registry category map (one state in LiveAnalysisTab) for the Indicators subgroup headers.
    categoryByIndicator = EMPTY_CATEGORY_MAP,
}) => {
    const [activeTab, setActiveTab] = React.useState('market_data');
    const [openGroup, setOpenGroup] = React.useState('core');
    const [moreOpen, setMoreOpen] = React.useState(false);
    const uid = React.useId();
    const [sideFilter, setSideFilter] = React.useState('ALL');
    const authoringGroups = React.useMemo(
        () => groupAuthoringItems(nodes, categoryByIndicator),
        [nodes, categoryByIndicator],
    );
    const indicatorAuthoringGroups = authoringGroups.filter((group) => group.group === AUTHORING_GROUP.INDICATORS);
    const strategyLogicAuthoringGroups = authoringGroups.filter((group) => group.group === AUTHORING_GROUP.STRATEGY_LOGIC);
    const mathUsesIndicatorDecisionHeader = strategyLogicAuthoringGroups.length === 0
        && indicatorAuthoringGroups.some((group) => group.nodes.some((node) => node.type === 'math'));

    // --> Filter trades by side (ALL/LONG/SHORT)
    const filteredTrades = React.useMemo(() => {
        if (!backtestResults?.trades || sideFilter === 'ALL') return backtestResults?.trades || [];
        return backtestResults.trades.filter(t => t.side === sideFilter);
    }, [backtestResults?.trades, sideFilter]);

    // --> Recalculate metrics when side-filtered (skip if no trades excluded)
    const filteredMetrics = React.useMemo(() => {
        if (!backtestResults?.metrics) return null;
        if (sideFilter === 'ALL') return backtestResults.metrics;
        const allTrades = backtestResults?.trades || [];
        const excluded = allTrades.filter(t => t.side !== sideFilter);
        if (excluded.length === 0) return backtestResults.metrics;
        //! 144-1 F11: the recalculation is trade-derived; the run's cost-blind provenance is not,
        //!  so it must survive the side filter or the cost-blind line vanishes on filtered views.
        const recalculated = recalcMetricsFromTrades(filteredTrades, backtestResults?.initial_capital || 100000);
        const provenance = backtestResults.metrics.economic_provenance;
        return provenance ? { ...recalculated, economic_provenance: provenance } : recalculated;
    }, [backtestResults?.metrics, backtestResults?.trades, filteredTrades, sideFilter, backtestResults?.initial_capital]);

    // --> Adjust equity curve for side filter by subtracting excluded trades' P&L from original data
    const filteredChartData = React.useMemo(() => {
        if (sideFilter === 'ALL') return backtestResults?.chartData;
        const origData = backtestResults?.chartData;
        const allTrades = backtestResults?.trades || [];
        const ic = backtestResults?.initial_capital || backtestResults?.metrics?.initial_capital || 100000;
        // Trades NOT matching the filter — their P&L needs to be subtracted
        const excludedTrades = allTrades.filter(t => t.side !== sideFilter);
        // No exclusions → all trades match filter, return original chart unchanged
        if (excludedTrades.length === 0) return origData;
        // All trades excluded → flat line at initial capital
        if (excludedTrades.length === allTrades.length) {
            if (!origData?.length) return [];
            const toEpoch = (p) => { const ms = new Date(p?.time ?? p?.timestamp ?? p).getTime(); return Number.isFinite(ms) ? Math.floor(ms / 1000) : null; };
            const first = toEpoch(origData[0]);
            const last = toEpoch(origData[origData.length - 1]);
            if (first == null || last == null) return [];
            return [{ time: first, value: ic }, { time: last, value: ic }];
        }
        // Partial filter: adjust original curve by subtracting excluded trades' cumulative P&L
        const toSec = (t) => { if (!t) return Infinity; const ms = new Date(t).getTime(); return Number.isFinite(ms) ? Math.floor(ms / 1000) : Infinity; };
        const sortedExcluded = excludedTrades
            .map(t => ({ exitSec: toSec(t.exit_time ?? t.exitTime), pnl: Number(t.pnl) || 0 }))
            .sort((a, b) => a.exitSec - b.exitSec);
        return origData.map(point => {
            const pointTime = point?.time ?? point?.timestamp ?? point?.ts;
            const pointSec = toSec(pointTime);
            const pointVal = Number(point?.equity ?? point?.value ?? point?.balance) || 0;
            let excludedPnl = 0;
            for (const ex of sortedExcluded) {
                if (ex.exitSec <= pointSec) excludedPnl += ex.pnl;
                else break;
            }
            return { time: pointTime, value: pointVal - excludedPnl };
        });
    }, [sideFilter, backtestResults?.chartData, backtestResults?.trades, backtestResults?.initial_capital, backtestResults?.metrics?.initial_capital]);

    const booleanNodeIds = React.useMemo(() => {
        const ids = new Set();
        (nodes || []).forEach((node) => {
            if (['comparator', 'logic_gate', 'logic', 'signal', 'position_trigger'].includes(node?.type)) {
                ids.add(String(node.id));
            }
        });
        return ids;
    }, [nodes]);

    const data = React.useMemo(() => {
        const sanitizedIndicators = {};
        const hoverTime = hoverData?.time;
        const staleBooleanSuppressions = {};

        if (indicatorMaps) {
            Object.entries(indicatorMaps).forEach(([nodeId, map]) => {
                if (!(map instanceof Map)) return;

                let targetValue = undefined;

                if (hoverTime) {
                    // 1. Precise Match (O(1))
                    if (map.has(hoverTime)) {
                        targetValue = map.get(hoverTime);
                    } else {
                        // 2. Lookback for HTF/Padded data (e.g. find last 5m candle from 1m hover)
                        // Scan back up to 24 hours in 1m steps (very fast with Map.has)
                        for (let t = hoverTime - 60; t >= hoverTime - (24 * 3600); t -= 60) {
                            if (map.has(t)) {
                                targetValue = map.get(t);
                                break;
                            }
                        }
                    }
                } else {
                    // 3. Fallback to Latest Global Value
                    let maxT = -1;
                    map.forEach((_val, t) => {
                        if (t > maxT) {
                            maxT = t;
                            targetValue = _val;
                        }
                    });
                }

                // Boolean Supression Logic
                if (targetValue === undefined) {
                    if (shouldSuppressBooleanWarmup(nodeId, hoverTime, booleanNodeIds)) {
                        staleBooleanSuppressions[nodeId] = undefined;
                    }
                }

                sanitizedIndicators[nodeId] = targetValue;
            });
        }

        //! Merge precedence (later wins): executionData → latestData → sanitizedIndicators → staleBooleanSuppressions → hoverData.
        //! hoverData always wins when the user is hovering. sanitizedIndicators (from indicatorMaps, the unified store) wins over executionData for overlapping keys.
        const merged = {
            ...executionData,
            ...latestData || {},
            ...sanitizedIndicators,
            ...staleBooleanSuppressions,
            ...hoverData || {},
        };

        return merged;
    }, [indicatorMaps, executionData, hoverData, latestData, booleanNodeIds]);

    //--> 50-17: the hovered trade lookup the chart uses (SmartChart buildTradeSpanMap) over positionLevels.trades
    //*   (compactTradeLevels of the chart's levelHistory), Live Analysis only.
    const tradeSpanMap = React.useMemo(
        () => buildTradeSpanMap(positionLevels?.trades),
        [positionLevels],
    );

    //--> 50-17: valueAt(nodeId, barsAgo) — a node's value N bars before the evaluated bar (hovered bar, else
    //*   latest), from indicatorMaps: the SAME per-bar series the chart draws and `data` above reads. Bars are
    //*   the series' own keys, so N steps back is N bars of that node's timeframe. undefined = not in the series.
    const hoverTime = hoverData?.time ?? null;
    //* 50-17: latest bar of the per-bar series the chart draws (the evaluated bar when not hovering)
    const latestBarTime = React.useMemo(() => {
        let best = null;
        Object.values(indicatorMaps || {}).forEach((m) => {
            if (!(m instanceof Map)) return;
            m.forEach((_, k) => { if (typeof k === 'number' && (best == null || k > best)) best = k; });
        });
        return best;
    }, [indicatorMaps]);
    const sortedKeysRef = React.useRef(new WeakMap());
    const valueAt = React.useCallback((nodeId, barsAgo) => {
        const map = indicatorMaps?.[nodeId];
        if (!(map instanceof Map) || map.size === 0) return undefined;
        let cached = sortedKeysRef.current.get(map);
        if (!cached || cached.size !== map.size) {
            cached = { size: map.size, keys: Array.from(map.keys()).sort((a, b) => a - b) };
            sortedKeysRef.current.set(map, cached);
        }
        const { keys } = cached;
        let idx = keys.length - 1;
        if (hoverTime != null) {
            //* last bar at or before the hovered time (an HTF series has no key on every chart bar)
            let lo = 0;
            let hi = keys.length - 1;
            idx = -1;
            while (lo <= hi) {
                const mid = (lo + hi) >> 1;
                if (keys[mid] <= hoverTime) { idx = mid; lo = mid + 1; } else hi = mid - 1;
            }
        }
        const i = idx - Math.trunc(Number(barsAgo) || 0);
        return idx < 0 || i < 0 ? undefined : map.get(keys[i]);
    }, [indicatorMaps, hoverTime]);

    const resolveMathInputs = React.useCallback((node) => {
        return resolveMathInputsUtil(node, nodes, edges, data, executionData, latestData, visualOverrides);
    }, [nodes, edges, data, executionData, latestData, visualOverrides]);

    const resolveLogicInputs = React.useCallback((node) => {
        return resolveLogicInputsUtil(node, nodes, edges, data, executionData, latestData, visualOverrides);
    }, [nodes, edges, data, executionData, latestData, visualOverrides]);

    const displayedKeys = React.useMemo(() => {
        const keys = new Set();
        OHLC_KEYS.forEach(k => keys.add(k));
        DEPTH_KEYS.forEach(k => keys.add(k));
        COMMON_ANALYTICS.forEach(k => keys.add(k));
        if (categorizedNodes) {
            Object.values(categorizedNodes).flat().forEach(node => keys.add(node.id));
        }
        return keys;
    }, [categorizedNodes]);

    const extraMetricKeys = React.useMemo(() => {
        if (!data) return [];
        const keys = Object.keys(data);
        if (keys.length > 100) return [];

        return keys.filter(key => {
            if (displayedKeys.has(key) || EXCLUDED_KEYS.has(key) || key.endsWith('_at')) return false;
            const val = data[key];
            return typeof val === 'number' || (typeof val === 'string' && !isNaN(parseFloat(val)) && isFinite(val));
        }).sort();
    }, [displayedKeys, (data ? Object.keys(data).sort().join(',') : null)]);

    const extraMetrics = React.useMemo(() => {
        return extraMetricKeys.map(key => [key, data[key]]);
    }, [extraMetricKeys, data]);

    const dataSourceNode = React.useMemo(() => {
        if (categorizedNodes?.dataSource?.length > 0) {
            return categorizedNodes.dataSource[0];
        }
        if (categorizedNodes) {
             const allNodes = Object.values(categorizedNodes).flat();
             return allNodes.find(n => n.type === 'data_source');
        }
        return null;
    }, [categorizedNodes]);

    const handleMarketDataClick = React.useCallback(() => {
        if (!onItemClick) return;
        if (dataSourceNode) {
            onItemClick(dataSourceNode);
        } else {
            onItemClick({ id: 'market_data', type: 'data_source', data: { label: 'Market Data' } });
        }
    }, [dataSourceNode, onItemClick]);

    const isMarketDataActive = selectedNodeId === 'market_data' || (dataSourceNode && selectedNodeId === dataSourceNode.id);

    const metrics = backtestResults?.metrics;

    React.useEffect(() => {
        if (isMetricsExpanded && metrics && activeTab !== 'period_analysis' && activeTab !== 'trade_logs') {
            setActiveTab('analytics');
        } else if (!isMetricsExpanded && activeTab === 'analytics') {
            setActiveTab('market_data');
        }
    }, [isMetricsExpanded, metrics]);


    /*>>> MARKET-DATA SECTION BODIES (50-12: each body defined once, composed by strip or responsive layout) <<<*/
    const renderCoreBody = () => (
        <>
            {OHLC_KEYS.filter((key) => data?.[key] != null).map(key => (
                <CompactIsoCard
                    key={key}
                    label={key}
                    value={data?.[key]}
                    onClick={handleMarketDataClick}
                    isActive={isMarketDataActive}
                />
            ))}
        </>
    );

    const renderIndicatorsBody = (list = [...(categorizedNodes?.indicators || []), ...(categorizedNodes?.custom || [])]) => (
        <>
            {list.map(node => {
                const nodeTf = node.data.timeframe;
                const showBadge = nodeTf && primaryTimeframe && nodeTf !== primaryTimeframe;
                const isHidden = !isNodeShownOnChart(node.type, node.data); // 50-7 one rule

                let displayValue;
                if (node.data.isFieldExtractor && node.data.params?.field) {
                    displayValue = data?.[node.data.params.field];
                } else {
                    displayValue = data?.[node.id];
                    if (Array.isArray(displayValue) && displayValue.length > 0) {
                        displayValue = displayValue[displayValue.length - 1]?.value;
                    }
                }

                //! 142-37: a LIVE model dict wins over a history-series scalar (never collapse
                //!  a multi-output model to one number); hover still shows the hovered bar.
                const liveValue = liveNodeOutputs?.[node.id] ?? executionData?.[node.id];
                //! 142-37 loop 4 (Codex consult 10-15): a hovered LEGACY scalar history point must not
                //!  collapse an available live model dict; only a dict-valued hovered point wins.
                const hoverValue = hoverData?.[node.id];
                if (!node.data.isFieldExtractor && isModelOutputDict(liveValue) && !isModelOutputDict(hoverValue)) {
                    displayValue = liveValue;
                }

                if (isModelOutputDict(displayValue)) {
                    const tile = buildModelTile(node, displayValue, nodes, edges, allIndicatorMetadata?.find(m => m.name === node.data?.indicator)?.visual_settings?.output_labels);
                    return (
                        <CompositeIndicatorCard
                            key={node.id}
                            label={node.data.label || 'Model'}
                            values={tile.values}
                            primaryValue={tile.primaryValue}
                            highlightKey={tile.highlightKey}
                            annotations={tile.annotations}
                            onClick={() => onItemClick && onItemClick(node)}
                            isActive={selectedNodeId === node.id}
                            color="purple"
                            badge={tile.badge || (showBadge ? nodeTf : null)}
                            isHidden={isHidden}
                        />
                    );
                }

                if (typeof displayValue === 'object' && displayValue !== null) {
                    if (displayValue.long_stop !== undefined && displayValue.short_stop !== undefined) {
                        const dir = displayValue.direction === 1 ? '▲' : '▼';
                        const val = (displayValue.direction === 1 ? displayValue.long_stop : displayValue.short_stop);
                        return (
                            <CompactIsoCard
                                key={node.id}
                                label={node.data.label || 'Ind'}
                                value={`${dir} ${val?.toFixed(2) || '0.00'}`}
                                onClick={() => onItemClick && onItemClick(node)}
                                isActive={selectedNodeId === node.id}
                                color="purple"
                                badge={showBadge ? nodeTf : null}
                                isHidden={isHidden}
                            />
                        );
                    }

                    return (
                        <CompositeIndicatorCard
                            key={node.id}
                            label={node.data.label || 'Indicator'}
                            values={displayValue}
                            onClick={() => onItemClick && onItemClick(node)}
                            isActive={selectedNodeId === node.id}
                            color="purple"
                            badge={showBadge ? nodeTf : null}
                            isHidden={isHidden}
                        />
                    );
                }

                return (
                    <CompactIsoCard
                       key={node.id}
                       label={node.data.label || 'Ind'}
                       value={displayValue}
                       onClick={() => onItemClick && onItemClick(node)}
                       isActive={selectedNodeId === node.id}
                       color="purple"
                       badge={showBadge ? nodeTf : null}
                       isHidden={isHidden}
                    />
                );
            })}
        </>
    );

    const renderMathBody = (list = categorizedNodes.math) => (
        <>
            {list.map(node => {
                const resultVal = data?.[node.id];
                const isHidden = !isNodeShownOnChart(node.type, node.data); // 50-7 one rule
                const opMap = { 'ADD': '+', 'SUB': '-', 'MUL': '×', 'DIV': '÷', 'GT': '>', 'LT': '<' };
                const rawOp = node.data.operator || node.data.operation || 'ADD';
                const op = rawOp.toUpperCase();
                const opIcon = opMap[op] || rawOp;

                const { name: finalLabel } = displayLabelOf(node, nodes, edges, visualOverrides);
                const detailedValues = resolveMathInputs(node);

                return (
                    <CompositeIndicatorCard
                        key={node.id}
                        label={finalLabel}
                        values={detailedValues}
                        primaryValue={resultVal}
                        onClick={() => onItemClick && onItemClick(node)}
                        isActive={selectedNodeId === node.id}
                        color="amber"
                        badge={opIcon}
                        isHidden={isHidden}
                    />
                );
            })}
        </>
    );

    const renderLogicBody = (list = categorizedNodes.logic) => (
        <>
            {list.map(node => {
                const detailedValues = resolveLogicInputs(node);
                const isHidden = !isNodeShownOnChart(node.type, node.data); // 50-7 one rule
                const opMap = { 'GT': '>', 'LT': '<', 'GTE': '>=', 'LTE': '<=', 'EQ': '==', 'NEQ': '!=' };
                const rawOp = node.data.operator || node.data.params?.operator || 'GT';
                const opKey = typeof rawOp === 'string' ? rawOp.toUpperCase() : 'GT';
                const opIcon = opMap[opKey] || opKey;

                const rawResult = resolveNodeStatusRaw(node.id, hoverData, executionData, data);
                const normalizedStatus = normalizeBooleanStatus(rawResult);
                const isNodeValid = normalizedStatus !== null;
                const { name: logicName, autoText: logicAutoText } = displayLabelOf(node, nodes, edges, visualOverrides);

                return (
                    <CompositeIndicatorCard
                        key={node.id}
                        label={logicName}
                        labelTitle={logicAutoText}
                        values={detailedValues}
                        primaryValue={isNodeValid ? (normalizedStatus ? 'TRUE' : 'FALSE') : 'WAIT'}
                        onClick={() => onItemClick && onItemClick(node)}
                        isActive={selectedNodeId === node.id}
                        color={isNodeValid ? (normalizedStatus ? "emerald" : "red") : "gray"}
                        badge={opIcon}
                        isHidden={isHidden}
                    />
                );
            })}
        </>
    );

    const renderStrategyLogicBody = () => (
        <>
            <EngineDecisionHeader engineDecision={engineDecision} />
            <PositionGroup
                title="Long Entry"
                type="long"
                nodes={categorizedNodes.longEntry}
                hoverData={hoverData}
                mergedData={data}
                executionData={executionData}
                latestData={latestData}
                onItemClick={onItemClick}
                selectedNodeId={selectedNodeId}
                allNodes={nodes}
                allEdges={edges}
                visualOverrides={visualOverrides}
                liveNodeInvalidReasons={liveNodeInvalidReasons}
                liveNodeOutputs={liveNodeOutputs}
                positionLevels={positionLevels}
                tradeSpanMap={tradeSpanMap}
                valueAt={valueAt}
                latestBarTime={latestBarTime}
            />
            <PositionGroup
                title="Long Exit"
                type="long"
                nodes={categorizedNodes.longExit}
                hoverData={hoverData}
                mergedData={data}
                executionData={executionData}
                latestData={latestData}
                onItemClick={onItemClick}
                selectedNodeId={selectedNodeId}
                allNodes={nodes}
                allEdges={edges}
                visualOverrides={visualOverrides}
                liveNodeInvalidReasons={liveNodeInvalidReasons}
                liveNodeOutputs={liveNodeOutputs}
                positionLevels={positionLevels}
                tradeSpanMap={tradeSpanMap}
                valueAt={valueAt}
                latestBarTime={latestBarTime}
            />
             <PositionGroup
                title="Short Entry"
                type="short"
                nodes={categorizedNodes.shortEntry}
                hoverData={hoverData}
                mergedData={data}
                executionData={executionData}
                latestData={latestData}
                onItemClick={onItemClick}
                selectedNodeId={selectedNodeId}
                allNodes={nodes}
                allEdges={edges}
                visualOverrides={visualOverrides}
                liveNodeInvalidReasons={liveNodeInvalidReasons}
                liveNodeOutputs={liveNodeOutputs}
                positionLevels={positionLevels}
                tradeSpanMap={tradeSpanMap}
                valueAt={valueAt}
                latestBarTime={latestBarTime}
            />
            <PositionGroup
                title="Short Exit"
                type="short"
                nodes={categorizedNodes.shortExit}
                hoverData={hoverData}
                mergedData={data}
                executionData={executionData}
                latestData={latestData}
                onItemClick={onItemClick}
                selectedNodeId={selectedNodeId}
                allNodes={nodes}
                allEdges={edges}
                visualOverrides={visualOverrides}
                liveNodeInvalidReasons={liveNodeInvalidReasons}
                liveNodeOutputs={liveNodeOutputs}
                positionLevels={positionLevels}
                tradeSpanMap={tradeSpanMap}
                valueAt={valueAt}
                latestBarTime={latestBarTime}
            />
        </>
    );

    //--> Responsive-only authoring taxonomy. Indicators and math share the same combined result,
    //*   so registry-custom indicators and math cannot diverge between dashboard consumers.
    const renderIndicatorsGroupedBody = () => (
        <>
            {mathUsesIndicatorDecisionHeader && <EngineDecisionHeader engineDecision={engineDecision} />}
            {indicatorAuthoringGroups.map((g) => (
                <div
                    key={g.id}
                    data-testid="rdwatch-subgroup"
                    data-subgroup={g.title}
                    data-authoring-group-id={g.id}
                    className="mb-2 last:mb-0"
                >
                    <SubgroupHeader title={g.title} />
                    {renderIndicatorsBody(g.nodes.filter((node) => node.type !== 'math'))}
                    {renderMathBody(g.nodes.filter((node) => node.type === 'math'))}
                </div>
            ))}
        </>
    );

    //--> Strategy Logic uses the other half of the same combined grouping result. categorizeNodes
    //*   remains only the Entry/Exit label + active-highlight authority.
    const renderStrategyLogicSubgroupsBody = () => (
        <>
            <EngineDecisionHeader engineDecision={engineDecision} />
            {strategyLogicAuthoringGroups.map((g) => {
                const groupProps = {
                    hoverData, mergedData: data, executionData, latestData,
                    onItemClick, selectedNodeId, allNodes: nodes, allEdges: edges, liveNodeInvalidReasons, liveNodeOutputs, positionLevels, tradeSpanMap, valueAt, visualOverrides,
                };
                let content;
                if (g.subgroup === LOGIC_SUBGROUP.MATH_LOGIC) {
                    content = (
                        <>
                            {renderMathBody(g.nodes.filter((n) => n.type === 'math'))}
                            {renderLogicBody(g.nodes.filter((n) => n.type !== 'math'))}
                        </>
                    );
                } else if (g.subgroup === LOGIC_SUBGROUP.LONG || g.subgroup === LOGIC_SUBGROUP.SHORT) {
                    const side = g.subgroup === LOGIC_SUBGROUP.LONG ? 'long' : 'short';
                    const Side = g.subgroup === LOGIC_SUBGROUP.LONG ? 'Long' : 'Short';
                    const entryIds = new Set((categorizedNodes?.[`${side}Entry`] || []).map((n) => n.id));
                    const exitIds = new Set((categorizedNodes?.[`${side}Exit`] || []).map((n) => n.id));
                    //* Mirror the Builder strip: Entry/Exit rows get the FULL side arrays (wired comparators /
                    //* gates as chips, not only the signal node); Entry wins over Exit.
                    const entryList = categorizedNodes?.[`${side}Entry`] || [];
                    const exitList = (categorizedNodes?.[`${side}Exit`] || []).filter((n) => !entryIds.has(n.id));
                    const rest = g.nodes.filter((n) => !entryIds.has(n.id) && !exitIds.has(n.id));
                    content = (
                        <>
                            {[['Entry', entryList], ['Exit', exitList]].map(([kind, list]) => (
                                <PositionGroup key={kind} title={`${Side} ${kind}`} type={side} nodes={list} {...groupProps} />
                            ))}
                            <PositionGroup key="rest" title="" type={side} nodes={rest} {...groupProps} />
                        </>
                    );
                } else {
                    content = <PositionGroup key="rest" title="" nodes={g.nodes} {...groupProps} />;
                }
                return (
                    <div
                        key={g.id}
                        data-testid="rdwatch-subgroup"
                        data-subgroup={g.title}
                        data-authoring-group-id={g.id}
                        className="mb-2 last:mb-0"
                    >
                        <SubgroupHeader title={g.title} />
                        {content}
                    </div>
                );
            })}
        </>
    );

    const renderKeyAnalyticsBody = () => (
        <>
            {COMMON_ANALYTICS.filter((key) => data?.[key] != null).map(key => (
                 <CompactIsoCard
                    key={key}
                    label={key}
                    value={data?.[key]}
                    onClick={handleMarketDataClick}
                    isActive={isMarketDataActive}
                    color="indigo"
                />
            ))}
        </>
    );

    const renderExtraBody = () => (
        <>
            {extraMetrics.map(([key, val]) => (
                <CompactIsoCard
                    key={key}
                    label={key}
                    value={val}
                    onClick={handleMarketDataClick}
                    isActive={isMarketDataActive}
                    color="amber"
                />
            ))}
        </>
    );

    const renderSystemTraceBody = () => (
        <>
            <div className="flex flex-col gap-2 h-full">
                <div className="mt-1">
                    <details className="group" open>
                        <summary className="flex items-center gap-2 cursor-pointer text-[9px] font-bold text-fg-3 uppercase tracking-wider hover:text-[var(--node-comparator)] transition-colors">
                            <span className="group-open:rotate-90 transition-transform">▶</span>
                            Market Depth
                        </summary>
                        <div className="pl-2 pt-2 flex flex-wrap gap-2">
                            {Object.entries(data || {}).map(([key, val]) => {
                                if (DEPTH_KEYS.includes(key)) {
                                    return (
                                        <div key={key} className="flex flex-col bg-bg-1 rounded border border-[var(--divider)] p-1.5 overflow-hidden max-w-[120px]">
                                            <span className="text-[9px] font-bold text-fg-3 uppercase truncate mb-1">{key}</span>
                                            <div className="text-[8px] font-mono text-fg-2 dark:text-fg-3 truncate">
                                                {Array.isArray(val) ? `[${val.length} items]` : JSON.stringify(val)}
                                            </div>
                                        </div>
                                    );
                                }
                                return null;
                            })}
                        </div>
                    </details>
                </div>

                <div className="mt-1">
                    <details className="group">
                        <summary className="flex items-center gap-2 cursor-pointer text-[9px] font-bold text-fg-3 uppercase tracking-wider hover:text-[var(--node-comparator)] transition-colors">
                            <span className="group-open:rotate-90 transition-transform">▶</span>
                            Latency & Trace
                        </summary>
                        <div className="pl-2 pt-2 flex flex-wrap gap-2">
                            {Object.entries(data || {}).map(([key, val]) => {
                                if (key.endsWith('_at') || ['source', 'tick_type'].includes(key)) {
                                    return (
                                        <div
                                            key={key}
                                            className="flex flex-col rounded border p-1.5 min-w-[100px] bg-bg-1 border-[var(--divider)]"
                                        >
                                            <span className="text-[9px] font-bold text-fg-3 uppercase truncate">{key.replace('_at', '')}</span>
                                            <span className="text-[10px] font-mono text-fg-2 dark:text-fg-3 truncate" title={String(val)}>
                                                {String(val).split('T')[1]?.split('.')[0] || String(val)}
                                            </span>
                                        </div>
                                    );
                                }
                                return null;
                            })}
                        </div>
                    </details>
                </div>

                {(!data) && (
                    <div className="text-[10px] text-fg-3 italic p-2">Waiting for data...</div>
                )}
            </div>
        </>
    );

    /*>>> RESPONSIVE MARKET-DATA LAYOUT (50-12) <<<*/
    //* Tier comes from the one app authority (hooks/useBreakpoint). The responsive renderer is opt-in:
    //  default consumers (Strategy Builder, PopOutDashboard) keep the horizontal strip untouched.
    const { isPhone } = useBreakpoint();
    const layoutMode = panelMode ? 'accordion' : (!responsiveLayout ? 'strip' : (isPhone ? 'accordion' : 'grid'));

    const hasIndicators = (categorizedNodes?.indicators?.length > 0 || categorizedNodes?.custom?.length > 0);
    const hasAuthoringIndicators = indicatorAuthoringGroups.length > 0;
    const hasMath = categorizedNodes?.math?.length > 0;
    const hasLogic = categorizedNodes?.logic?.length > 0;
    const hasStrategyLogic = (categorizedNodes?.longEntry?.length > 0 || categorizedNodes?.longExit?.length > 0 || categorizedNodes?.shortEntry?.length > 0 || categorizedNodes?.shortExit?.length > 0);
    const hasKeyAnalytics = COMMON_ANALYTICS.some((key) => data?.[key] != null);
    //-->  Exactly the keys the System Trace body renders (depth + latency/trace) — the secondary
    //     section is omitted (and with it the Show more / More control) when nothing would render.
    const hasSystemTrace = React.useMemo(() => Object.keys(data || {}).some(
        (key) => DEPTH_KEYS.includes(key) || key.endsWith('_at') || ['source', 'tick_type'].includes(key)
    ), [data]);

    //-->  Legacy strip: eight sections, original order and widths (default consumers).
    const stripSections = [
        { key: 'core', title: 'Core Data', icon: FiDatabase, color: 'blue', width: 'w-[140px]', visible: true, body: renderCoreBody },
        { key: 'indicators', title: 'Indicators', icon: FiLayers, color: 'purple', width: 'w-[160px]', visible: hasIndicators, body: renderIndicatorsBody },
        { key: 'math', title: 'Math Ops', icon: FiPercent, color: 'amber', width: 'w-[150px]', visible: hasMath, body: renderMathBody },
        { key: 'logic', title: 'Logic Ops', icon: FiShuffle, color: 'purple', width: 'w-[160px]', visible: hasLogic, body: renderLogicBody },
        { key: 'strategy', title: 'Strategy Logic', icon: FiTrendingUp, color: 'emerald', width: 'w-[180px]', visible: hasStrategyLogic, body: renderStrategyLogicBody },
        { key: 'analytics', title: 'Key Analytics', icon: FiActivity, color: 'indigo', width: 'w-[160px]', visible: hasKeyAnalytics, body: renderKeyAnalyticsBody },
        { key: 'extra', title: 'Extra Metrics', icon: FiActivity, color: 'amber', width: 'w-[160px]', visible: extraMetrics.length > 0, body: renderExtraBody },
        { key: 'trace', title: 'System Trace', icon: FiHash, color: 'gray', width: 'flex-1 min-w-[250px]', visible: true, body: renderSystemTraceBody },
    ];

    //-->  Responsive: the SAME bodies composed into three primary groups + two secondary sections.
    const responsiveSections = [
        { key: 'core', title: 'Core Data', icon: FiDatabase, color: 'blue', secondary: false, visible: true,
          body: () => (<>{renderCoreBody()}{hasKeyAnalytics && renderKeyAnalyticsBody()}</>) },
        { key: 'indicators', title: 'Indicators', icon: FiLayers, color: 'purple', secondary: false, visible: hasAuthoringIndicators, body: renderIndicatorsGroupedBody },
        { key: 'strategy', title: 'Strategy Logic', icon: FiTrendingUp, color: 'emerald', secondary: false, visible: strategyLogicAuthoringGroups.length > 0, body: renderStrategyLogicSubgroupsBody },
        { key: 'extra', title: 'Extra Metrics', icon: FiActivity, color: 'amber', secondary: true, visible: extraMetrics.length > 0, body: renderExtraBody },
        { key: 'trace', title: 'System Trace', icon: FiHash, color: 'gray', secondary: true, visible: hasSystemTrace, body: renderSystemTraceBody },
    ];

    const renderResponsiveMarketData = () => {
        const primary = responsiveSections.filter((s) => s.visible && !s.secondary);
        const secondary = responsiveSections.filter((s) => s.visible && s.secondary);
        const hasSecondary = secondary.length > 0;
        const toggleMore = () => setMoreOpen((v) => !v);

        if (layoutMode === 'grid') {
            const shown = moreOpen ? [...primary, ...secondary] : primary;
            return (
                <div data-testid="rdwatch-responsive" className="flex flex-col h-full w-full min-w-0 overflow-y-auto overflow-x-hidden custom-scrollbar">
                    <div
                        data-testid="rdwatch-grid"
                        className="grid w-full min-w-0 overflow-x-hidden"
                        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '8px', alignContent: 'start' }}
                    >
                        {shown.map((s) => (
                            <DashboardSection key={s.key} mode="grid" title={s.title} icon={s.icon} color={s.color}>
                                {s.body()}
                            </DashboardSection>
                        ))}
                    </div>
                    {hasSecondary && (
                        <div className="sticky bottom-0 mt-auto flex justify-end px-2 py-1 bg-bg-2 border-t border-[var(--divider)]">
                            <button
                                type="button"
                                aria-expanded={moreOpen}
                                onClick={toggleMore}
                                className="min-h-[32px] px-3 text-[10px] font-bold uppercase tracking-wider text-fg-3 hover:text-fg-1 flex items-center gap-1"
                            >
                                {moreOpen ? 'Show less' : 'Show more'}
                            </button>
                        </div>
                    )}
                </div>
            );
        }

        //-->  accordion (phone): single-open primary groups + independent More disclosure.
        return (
            <div data-testid="rdwatch-responsive" className="flex flex-col h-full w-full min-w-0 overflow-y-auto overflow-x-hidden custom-scrollbar">
                {primary.map((s) => (
                    <DashboardSection
                        key={s.key}
                        mode="accordion"
                        title={s.title}
                        icon={s.icon}
                        color={s.color}
                        open={openGroup === s.key}
                        onToggle={() => setOpenGroup((g) => (g === s.key ? null : s.key))}
                        sectionId={`${uid}-${s.key}`}
                    >
                        {s.body()}
                    </DashboardSection>
                ))}
                {hasSecondary && (
                    <>
                        <button
                            type="button"
                            aria-expanded={moreOpen}
                            aria-controls={`${uid}-more`}
                            onClick={toggleMore}
                            className="w-full min-h-[44px] flex items-center gap-2 px-3 bg-bg-3 border-b border-[var(--divider)] text-left"
                        >
                            <span className="text-[10px] font-bold text-fg-3 uppercase tracking-widest">More</span>
                            <FiChevronRight size={14} className={`ml-auto text-fg-3 transition-transform ${moreOpen ? 'rotate-90' : ''}`} />
                        </button>
                        {moreOpen && (
                            <div id={`${uid}-more`}>
                                {secondary.map((s) => (
                                    <DashboardSection key={s.key} mode="accordion" title={s.title} icon={s.icon} color={s.color}>
                                        {s.body()}
                                    </DashboardSection>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    };
    return (
        <div className="h-full w-full bg-bg-2 flex flex-col border-t border-[var(--divider)] relative">

            {backtestResults && (
                <div className="flex border-b border-[var(--divider)] bg-bg-3/50 shrink-0">
                    <button
                        onClick={() => setActiveTab('market_data')}
                        className={`px-4 py-2 text-xs font-bold transition-all ${activeTab === 'market_data' ? 'border-b-2 border-[var(--node-visual)] text-[var(--node-visual)] bg-bg-2' : 'text-fg-3 hover:text-fg-2'}`}
                    >
                        Market Data
                    </button>
                    {metrics && (
                        <button
                            onClick={() => setActiveTab('analytics')}
                            className={`px-4 py-2 text-xs font-bold transition-all ${activeTab === 'analytics' ? 'border-b-2 border-[var(--color-profit)] text-[var(--color-profit)] bg-bg-2' : 'text-fg-3 hover:text-fg-2'}`}
                        >
                            Analytics
                        </button>
                    )}
                    <button
                        onClick={() => setActiveTab('trade_logs')}
                        className={`px-4 py-2 text-xs font-bold transition-all ${activeTab === 'trade_logs' ? 'border-b-2 border-[var(--node-comparator)] text-[var(--node-comparator)] bg-bg-2' : 'text-fg-3 hover:text-fg-2'}`}
                    >
                        Trade Logs
                    </button>
                    <button
                        onClick={() => setActiveTab('performance')}
                        className={`px-4 py-2 text-xs font-bold transition-all ${activeTab === 'performance' ? 'border-b-2 border-[var(--warning)] text-[var(--warning)] bg-bg-2' : 'text-fg-3 hover:text-fg-2'}`}
                    >
                        Performance
                    </button>

                    {/* Side Filter Toggle */}
                    <div className="ml-auto flex items-center gap-1 px-2">
                        {['ALL', 'LONG', 'SHORT'].map(side => (
                            <button
                                key={side}
                                onClick={() => setSideFilter(side)}
                                className={`px-2 py-1 text-[10px] font-bold rounded transition-all ${
 sideFilter === side
                                        ? side === 'ALL' ? 'bg-[color-mix(in_oklch,var(--node-visual)_16%,transparent)] text-[var(--node-visual)]'
                                            : side === 'LONG' ? 'bg-[color-mix(in_oklch,var(--color-profit)_16%,transparent)] text-[var(--color-profit)]'
                                            : 'bg-[color-mix(in_oklch,var(--color-loss)_16%,transparent)] text-[var(--color-loss)]'
                                        : 'text-fg-3 hover:text-fg-2 dark:hover:text-fg-3'
                                }`}
                            >
                                {side}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <div className="flex-1 flex overflow-hidden relative">
                {/* 0. Common Control Sidebar (Mode, Popout, Expand) - Always Visible */}
                {!panelMode && (
                <div className="flex flex-col items-center gap-2 py-2 px-0 border-r border-[var(--divider)] w-12 bg-bg-3/50 shrink-0 overflow-visible h-full z-20">
                     <Tooltip content="Pop out dashboard" placement="right" className="shrink-0">
                        <button onClick={onPopOut} className="w-10 h-10 flex items-center justify-center rounded-lg text-fg-3 hover:text-[var(--node-visual)] hover:bg-bg-3 transition-all shrink-0" aria-label="Pop out dashboard">
                           <FiExternalLink size={16} />
                        </button>
                     </Tooltip>

                     <div className="w-6 h-px bg-[var(--divider)] shrink-0"></div>

                     {/* Fluid Toggle (Maximize/Minimize) */}
                     <Tooltip content={isMetricsExpanded ? "Minimize dashboard" : "Maximize dashboard"} placement="right" className="shrink-0">
                        <button
                           onClick={onToggleMetricsExpanded}
                           className="group flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-500 bg-[color-mix(in_oklch,var(--node-comparator)_10%,transparent)] text-[var(--node-comparator)] border border-[color-mix(in_oklch,var(--node-comparator)_30%,transparent)] hover:bg-[color-mix(in_oklch,var(--node-comparator)_16%,transparent)]"
                           aria-label={isMetricsExpanded ? "Minimize dashboard" : "Maximize dashboard"}
                        >
                           <AnimatePresence mode="wait">
                              <motion.div
                                 key={isMetricsExpanded ? 'minimize' : 'maximize'}
                                 className="flex items-center justify-center"
                                 initial={{ scale: 0.5, opacity: 0, rotate: -90 }}
                                 animate={{ scale: 1, opacity: 1, rotate: 0 }}
                                 exit={{ scale: 0.5, opacity: 0, rotate: 90 }}
                                 transition={{ duration: 0.2 }}
                              >
                                 {isMetricsExpanded ? <FiMinimize2 size={16} /> : <FiMaximize2 size={16} />}
                              </motion.div>
                           </AnimatePresence>
                        </button>
                     </Tooltip>

                     {/* Chart Vars Buttons */}
                     {categorizedNodes?.chartType?.length > 0 && (
                         <>
                            <div className="w-6 h-px bg-[var(--divider)] shrink-0"></div>
                            <div className="flex flex-col gap-2 w-full items-center">
                                {categorizedNodes.chartType.map(node => {
                                    const isActive = activeChartNodeId === node.id;
                                    let label = node.data.label;
                                    if (!label || label === 'Var') {
                                        const type = (node.type || '').replace('_node', '').toLowerCase();
                                        if (type.includes('candle')) label = 'Candle';
                                        else if (type.includes('line')) label = 'Line';
                                        else if (type.includes('area')) label = 'Area';
                                        else if (type.includes('heikin')) label = 'Heikin';
                                        else if (type.includes('volume')) label = 'Vol';
                                        else if (type.includes('bar')) label = 'Bar';
                                        else label = 'Chart';
                                    }

                                    return (
                                        <Tooltip key={node.id} content={node.data.label || node.type} placement="right" className="shrink-0">
                                            <button
                                                onClick={() => onItemClick && onItemClick(node)}
                                                className={`w-10 h-10 rounded-lg flex items-center justify-center text-[9px] font-bold transition-all border ${
 isActive
                                                    ? 'bg-[var(--node-visual)] border-[var(--node-visual)] text-white shadow-lg'
                                                    : 'bg-bg-3 border-[var(--divider)] text-fg-3 hover:border-[var(--node-visual)] hover:text-[var(--node-visual)]'
                                                }`}
                                                aria-label={node.data.label || node.type}
                                            >
                                                {label.substring(0, 3).toUpperCase()}
                                            </button>
                                        </Tooltip>
                                    );
                                })}
                            </div>
                         </>
                     )}
                </div>
                )}


                {metrics?.capital_exhausted && (
                    <div className="flex items-center gap-2 bg-[color-mix(in_oklch,var(--color-loss)_16%,transparent)] text-[var(--color-loss)] px-4 py-2 text-sm font-semibold border-b border-[color-mix(in_oklch,var(--color-loss)_40%,transparent)]">
                        <FiAlertTriangle className="w-5 h-5 animate-pulse" />
                        <div>
                            Trading Halted: Capital exhausted due to margin call/drawdowns.
                            <span className="font-normal opacity-80 ml-1">Consider increasing starting capital or adjusting lot sizes.</span>
                        </div>
                    </div>
                )}


                {metrics?.capital_exhausted && (
                    <div className="flex items-center gap-2 bg-[color-mix(in_oklch,var(--color-loss)_16%,transparent)] text-[var(--color-loss)] px-4 py-2 text-sm font-semibold border-b border-[color-mix(in_oklch,var(--color-loss)_40%,transparent)]">
                        <FiAlertTriangle className="w-5 h-5 animate-pulse" />
                        <div>
                            Trading Halted: Capital exhausted due to margin call/drawdowns.
                            <span className="font-normal opacity-80 ml-1">Consider increasing starting capital or adjusting lot sizes.</span>
                        </div>
                    </div>
                )}

                {/* Tab Contents Area */}
                <div className={`flex-1 flex ${layoutMode !== 'strip' && activeTab === 'market_data' ? 'overflow-x-hidden' : 'overflow-x-auto'} overflow-y-hidden relative h-full`}>
                    {activeTab === 'analytics' && metrics && (
                        <div className="absolute inset-0 z-10 bg-bg-2 p-6 overflow-y-auto custom-scrollbar">
                             <div className="max-w-7xl mx-auto">
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-xl font-bold flex items-center gap-2">
                                        <FiActivity className="text-[var(--color-profit)]" />
                                        Backtest Analytics
                                    </h2>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-fg-3">Expanded View</span>
                                        <div className="w-2 h-2 rounded-full bg-[var(--color-profit)] animate-pulse" />
                                    </div>
                                </div>
                                <BacktestAnalyticsPanel
                                    metrics={filteredMetrics}
                                    chartData={filteredChartData}
                                    buyHoldData={backtestResults?.buyHoldCurve}
                                    initialCapital={backtestResults?.initial_capital || backtestResults?.metrics?.initial_capital}
                                />
                            </div>
                        </div>
                    )}

                    {activeTab === 'market_data' && (
                        layoutMode !== 'strip' ? renderResponsiveMarketData() : (
                            <div className="flex h-full w-full overflow-x-auto overflow-y-hidden">
                                {stripSections.filter((s) => s.visible).map((s) => (
                                    <DashboardSection key={s.key} title={s.title} icon={s.icon} color={s.color} width={s.width}>
                                        {s.body()}
                                    </DashboardSection>
                                ))}
                            </div>
                        )
                    )}

                    {activeTab === 'trade_logs' && filteredTrades && (
                        <div className="w-full h-full overflow-auto bg-bg-3 dark:bg-bg-1/50 p-2">
                            <BacktestTradeLog
                                trades={filteredTrades}
                                onTradeSelect={onPeriodSelect}
                            />
                        </div>
                    )}

                    {activeTab === 'performance' && filteredMetrics && (
                        <div className="w-full h-full overflow-auto bg-bg-3 dark:bg-bg-1/50 p-2">
                            <BacktestPerformance
                                metrics={filteredMetrics}
                                trades={filteredTrades}
                                initialCapital={backtestResults?.initial_capital || 100000}
                                startDate={backtestConfig?.startDate}
                                endDate={backtestConfig?.endDate}
                                sideFilter={sideFilter}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});

export default RDWatchDashboard;
